import {Actions} from './Actions';
import {Func} from '../util';
import {OldSource, OldSourceStructure} from '@store/@types/alt/Source';

/**
 * "Abstract" class for Stores. Alt will provide these definitions
 * This only exists to provide types
 */
export class Store<State> {
  protected state: State;

  protected bindActions<Type extends Actions>(_actions: Type) {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected registerAsync(_source: any) {
    throw new Error('Method not implemented');
  }
  protected setState(_state: Partial<State>) {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected getInstance<T>(): T extends OldSourceStructure ? OldSource<T> : T {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected preventDefault(): void {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected exportPublicMethods(...args: any): void {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected waitFor(...args: any): void {
    throw new Error('Method not implemented');
  }

  /**
   * @deprecated
   */
  protected emitChange(...args: any): void {
    throw new Error('Method not implemented');
  }
}

/**
 * Derives signature for Success hook of an action to be registered
 * in the store.
 *
 * This method is called manually by the parent action, usually
 * as the resolved branch of the action's returning Promise.
 *
 * Success hooks always return void and always take one parameter,
 * which is the return of it's parent Action.
 *
 * If this return value is wrapped in a Promise, we unwrap it here.
 *
 * Ex.
 * type FetchData = (includeInactive?: boolean) => Promise<string[]>;
 *
 * type FetchDataSuccess = SuccessWrapper<FetchData>
 *   is equivalent to: (args: string[]) => void;
 *
 * @link StoreMethods used internally here
 */
type SuccessWrapper<Action> = Action extends Func
  ? ReturnType<Action> extends Promise<infer WrappedType>
    ? WrappedType extends void
      ? (...args: any) => void
      : (arg: WrappedType, ...rest: any) => void
    : (...args: any) => void
  : Action;

/**
 * Derives signature of the default store hook for an action from initial store
 *
 * This method is called when the parent action calls it's `dispatch` function.
 *
 * Init hooks share the same name as their parent action, and take the same
 * parameters. However, these parameters may or may not be transformed by alt
 * when used in stores. This type attempts to follow these rules.
 *
 * Actions with no parameters or a single parameter are unaffected.
 *
 * Actions with multiple parameters are (usually) transformed into a tuple
 * and passed as the first argument.
 *
 * Ex.
 * type FetchData = (userId: string, parameters: Params) => Promise<string[]>;
 *
 * type FetchDataSuccess = InitWrapper<FetchData>
 *   is equivalent to: (args: [string, Params]) => void;
 *
 * @link StoreMethods used internally here
 */
type InitWrapper<Action> = Action extends Func
  ? Parameters<Action> extends [any]
    ? // Single parameter functions (Unary)
      (unaryArgs: Parameters<Action>[0]) => void
    : // 2 or more parameters are transformed into a tuple
    Parameters<Action> extends [any, any?, ...any]
    ? (tupleArgs: Parameters<Action>) => void
    : // Functions with no Parameters (Nullary)
      () => void
  : Action;

/**
 * Derives signatures for all methods which must be implemented to support actions.
 *
 * Takes two parameters. Actions which we derive from, and a union of fields to omit.
 * Fields which satisfy the union will not have signatures generated, and will not
 * require an implementation.
 *
 * @link InitWrapper describes creation of the default hook
 * @link SuccessWrapper describes creation of the Success hook
 *
 * The Error hook is simply a function of (error: Error) => void
 *
 * Any fields which dont match the pattern are unaffected
 */
export type StoreMethods<Actions, Unimplemented extends keyof Actions = never> = {
  [Property in keyof Omit<Actions, Unimplemented>]: Actions[Property] extends Func
    ? Property extends `${string}Success`
      ? SuccessWrapper<Actions[Property]>
      : Property extends `${string}Error`
      ? (error: Error) => void
      : Property extends `${Property & string}`
      ? InitWrapper<Actions[Property]>
      : Actions[Property]
    : Actions[Property];
};

export type StoreState<T> = T extends Store<infer State> ? State : T;

/**
 * This type is able to infer the type used when extending Store and use it
 * Basically means we don't have to supply the type param manually
 */
export type ExportedStore<GivenStore> = GivenStore extends Store<infer State>
  ? PublicStoreInterface<State> & GivenStore
  : PublicStoreInterface<any> & GivenStore;

interface PublicStoreInterface<State> {
  displayName: string;
  getState(): State;
  /**
   * @deprecated
   */
  listen(store: Func): void;
  /**
   * @deprecated
   */
  unlisten(store: Func): void;
}
